import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { DateInput } from '@mantine/dates';
import { Group, Button, Container, TextInput, Title, Grid, GridCol } from '@mantine/core';
import { useForm } from '@mantine/form';
import { useSelector } from 'react-redux';
import { AppState } from '../../../../client-redux/reducers';
import { Language } from '../../../../languages/languageHandler';
import { LoginState } from '../../../../client-redux/login/reducer';
import {
  IHelpdeskEquipment,
  fetchHelpdeskEquipInfo,
  EquipmentRegistrationRequest,
  updateHelpdeskEquipment
} from '../../../../../libraries/helpdesk/equipments';
import ChooseEntityModal from '../../../components/choose-entity-modal';
import Input from '../../../../components/inputs';
import { formatDateToISO } from '../../../../utils/date';
import { useStatusOperDialog } from '../../../../context/status-oper-dialog';
import { StatusType } from '../../../../../libraries/zps';
import './styles.scss';

enum SELECTED_FIELD {
  ENTITY,
  MAINT_1,
  MAINT_2
}

const RegisterEquipmentHelpdesk: React.FC = () => {
  const lang = useSelector<AppState, Language>(state => state.lang.lang);
  const loginInfo = useSelector<AppState, LoginState>(state => state.login);
  const controller = useRef(new AbortController()).current;
  const { eid } = useParams<string>();
  const [equipment, setEquipment] = useState<IHelpdeskEquipment>();
  const [chooseEntityModal, setChooseEntityModal] = useState<boolean>(false);

  const [selectedField, setSelectedField] = useState<SELECTED_FIELD>(SELECTED_FIELD.ENTITY);
  const { handleApiError, showStatusOperDialog } = useStatusOperDialog();

  useEffect(() => {
    getEquipmentList();
    return () => {
      controller.abort();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getEquipmentList = () => {
    if (!loginInfo.token) return;

    fetchHelpdeskEquipInfo(Number(eid), controller)
      .then(({ data }) => {
        const statusOper = data?.statusOper;

        if (statusOper?.code === 0) {
          setEquipment(data?.list?.[0]);
        } else {
          showStatusOperDialog({
            type: StatusType.ERROR,
            message: statusOper?.message || lang.GLOBAL_ERROR,
            statusOper
          });
        }
      })
      .catch(error => {
        if (error.name === 'AbortError') return;
        handleApiError(error, lang.GLOBAL_ERROR || 'Unexpected error');
      });
  };

  const defaultExpDate = () => {
    if (equipment?.licenceExp) {
      return equipment?.licenceExp;
    } else {
      const newDate = new Date();
      newDate.setFullYear(newDate.getFullYear() + 10);
      return newDate.toISOString();
    }
  };

  const form = useForm({
    initialValues: {
      eid: 0,
      entid: 0,
      entidName: '',
      maintid: 0,
      maintidName: '',
      maintid2: 0,
      maintid2Name: '',
      shop: {
        sid: 0,
        name: ''
      },
      dateTimeExp: defaultExpDate(),
      posid: 0
      /*       maxCurrencies: 1
       */
    }
  });

  useEffect(() => {
    if (equipment) {
      form.setValues(prevValues => ({
        ...prevValues,
        entid: prevValues.entid || equipment?.entity?.entid,
        entidName: prevValues.entidName || equipment?.entity?.name || '',
        maintid: prevValues.maintid || equipment?.maintEntity?.entid,
        maintidName: prevValues.maintidName || equipment?.maintEntity?.name || '',
        maintid2: prevValues.maintid2 || equipment?.maintEntity2?.entid,
        maintid2Name: prevValues.maintid2Name || equipment?.maintEntity2?.name || '',
        shop: {
          sid: prevValues.shop?.sid || equipment?.sid || 0,
          name: prevValues.shop?.name || equipment?.sidDescription || ''
        },
        dateTimeExp: defaultExpDate(),
        posid: Number(prevValues.posid || equipment?.posid || 0)
        /*         maxCurrencies: prevValues.maxCurrencies ?? equipment?.maxCurrencies ?? 1
         */
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipment]);

  const submitForm = async (values: any) => {
    const request: EquipmentRegistrationRequest = {
      eid: Number(eid),
      entid: values.entid,
      maintid: values.maintid,
      maintid2: values.maintid2,
      shop: {
        sid: values.shop.sid,
        name: values.shop.name
      },
      dateTimeExp: formatDateToISO(values.dateTimeExp),
      posid: Number(values.posid)
    };

    const formController = new AbortController();

    try {
      const response = await updateHelpdeskEquipment(request, formController);

      const statusOper = response?.data?.statusOper;

      if (statusOper?.code !== 0) {
        showStatusOperDialog({
          type: StatusType.ERROR,
          message: lang.EDIT_EQUIPMENTS_ALERT_UPDATED_ERROR,
          statusOper
        });
        return;
      }

      // optional refresh after success
      setTimeout(() => {
        getEquipmentList();
      }, 500);
    } catch (error) {
      handleApiError(error, lang.EDIT_EQUIPMENTS_ALERT_UPDATED_ERROR);
    }
  };

  const chooseEntid = (entid: number, name?: string) => {
    switch (selectedField) {
      case SELECTED_FIELD.ENTITY:
        form.setValues({ entid: entid, entidName: name });
        return;
      case SELECTED_FIELD.MAINT_1:
        form.setValues({ maintid: entid, maintidName: name });
        return;
      case SELECTED_FIELD.MAINT_2:
        form.setValues({ maintid2: entid, maintid2Name: name });
        return;
    }
  };

  const toggleChooseEntityModal = () => {
    setChooseEntityModal(!chooseEntityModal);
    setSelectedField(SELECTED_FIELD.ENTITY);
  };

  const toggleChooseMaintModal = () => {
    setChooseEntityModal(!chooseEntityModal);
    setSelectedField(SELECTED_FIELD.MAINT_1);
  };

  const toggleChooseAgentModal = () => {
    setChooseEntityModal(!chooseEntityModal);
    setSelectedField(SELECTED_FIELD.MAINT_2);
  };

  const clearEntid = () => {
    form.setValues({ ...form.values, entid: 0, entidName: '' });
  };

  const clearMaintid = () => {
    form.setValues({ ...form.values, maintid: 0, maintidName: '' });
  };

  const clearMaintid2 = () => {
    form.setValues({ ...form.values, maintid2: 0, maintid2Name: '' });
  };

  const handleDateChange = (value: string | null) => {
    if (value) {
      const date = new Date(value);
      const formattedDate = formatDateToISO(date);
      form.setFieldValue('dateTimeExp', formattedDate);
    } else {
      form.setFieldValue('dateTimeExp', '');
    }
  };

  return (
    <Container fluid classNames={{ root: 'register-equip-helpdesk' }}>
      <ChooseEntityModal
        lang={lang}
        isOpen={chooseEntityModal}
        hideBtn={() => toggleChooseEntityModal()}
        chooseEntid={chooseEntid}
      />
      <Group>
        <form onSubmit={form.onSubmit(values => submitForm(values))} className="register-form">
          <Group>
            {equipment && (
              <Title order={4}>
                {lang.EQUIPMENT_ID}: {equipment.eid} SN: {equipment.sn} {lang.GLOBAL_TYPE}: {equipment.type} ENTID:
                {equipment.entity?.entid}
              </Title>
            )}
          </Group>

          <Group classNames={{ root: 'entities' }}>
            {/* Entity Selector */}
            <Input
              label={lang.USER_ENTITY}
              value={form.values.entid ? `${form.values.entid} - ${form.values.entidName}` : ''}
              disabled={false}
              isEditable
              search={toggleChooseEntityModal}
              onDelete={clearEntid}
            />

            {/* Maintenance Company Selector */}
            <Input
              label={lang.HELPDESK_MAINTENANCE_COMPANY}
              value={form.values.maintid ? `${form.values.maintid} - ${form.values.maintidName}` : ''}
              disabled={false}
              isEditable
              search={toggleChooseMaintModal}
              onDelete={clearMaintid}
            />

            {/* Agent Selector */}
            <Input
              label={lang.HELPDESK_MAINTENANCE_AGENCY}
              value={form.values.maintid2 ? `${form.values.maintid2} - ${form.values.maintid2Name}` : ''}
              disabled={false}
              isEditable
              search={toggleChooseAgentModal}
              onDelete={clearMaintid2}
            />
          </Group>

          {/* Delegation */}
          <Grid classNames={{ root: 'extras' }}>
            {/* ID */}
            <GridCol span={{ base: 12, md: 6, lg: 6 }}>
              <TextInput label={'ID'} placeholder="ID" {...form.getInputProps('shop.sid')} />
            </GridCol>

            {/* Name */}
            <GridCol span={{ base: 12, md: 6, lg: 6 }}>
              <TextInput
                label={lang.GLOBAL_NAME}
                placeholder={`${lang.GLOBAL_NAME}`}
                {...form.getInputProps('shop.name')}
              />
            </GridCol>

            {/* Licence */}
            <GridCol span={{ base: 12, md: 6, lg: 6 }}>
              <DateInput
                label={lang.GLOBAL_LICENSE}
                value={form.values.dateTimeExp ? new Date(form.values.dateTimeExp) : null}
                onChange={handleDateChange}
                valueFormat="DD/MM/YYYY HH:mm"
              />
            </GridCol>

            {/* Posid */}
            <GridCol span={{ base: 12, md: 6, lg: 6 }}>
              <TextInput label="POSID" placeholder="Posid" {...form.getInputProps('posid')} />
            </GridCol>

            {/*     <GridCol span={{ base: 12, md: 4, lg: 4 }}>
              <TextInput
                label={lang.EQUIPMENT_MAX_CURRENCIES}
                placeholder={lang.EQUIPMENT_MAX_CURRENCIES}
                {...form.getInputProps('maxCurrencies')}
              />
            </GridCol> */}
          </Grid>

          <Group>
            <Button radius="md" type="submit" color="#538539">
              {lang.GLOBAL_SAVE_BUTTON}
            </Button>
          </Group>
        </form>
      </Group>
    </Container>
  );
};

export default RegisterEquipmentHelpdesk;
